/**
 * Paradigm Entity Registry
 *
 * Single source of truth for every object type in the platform.
 * Icon, label, color — all defined here. Import from this file
 * instead of hardcoding entity metadata elsewhere.
 *
 * Icons use Lucide React — clean, consistent SVG line icons.
 *
 * Usage:
 *   import { getEntity, getEntityIcon, getEntityLabel } from '../constants/entityRegistry';
 *   import { EntityIcon } from '../components/common';
 *
 *   const meta = getEntity('node');      // { label, plural, icon, color, bg }
 *   <EntityIcon type="node" />           // renders the Lucide icon
 *   <EntityIcon type="node" showLabel /> // renders icon + label in a badge
 */

import React from 'react';
import {
  ArrowLeftRight,
  LayoutList,
  File,
  User,
  UserCheck,
  Users,
  AppWindow,
  Bot,
  Puzzle,
  Zap,
  KeyRound,
  ShieldCheck,
  Search,
  HelpCircle,
  Plus,
  Pencil,
  Trash2,
  Eye,
  Share2,
  Link,
  Box,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Entity Types
// ---------------------------------------------------------------------------

const ENTITY_TYPES = {
  // --- Data objects ---
  node: {
    label: 'Node',
    plural: 'Nodes',
    // THE ISO CUBE, not a ring-and-dot (owner call): a node is an OBJECT, and
    // the cube is the mark of objecthood the ecosystem already draws for one —
    // the kit promoted the same silhouette to NucleusIcon for the rail seat.
    // Lucide's Box is that glyph in this file's own vocabulary (Lucide is a
    // Feather fork; Box is Feather's `box`), so the registry stays one library.
    icon: Box,
    color: '#2d6a8a',
    bg: 'rgba(45, 106, 138, 0.10)',
  },
  relationship: {
    label: 'Relationship',
    plural: 'Relationships',
    icon: ArrowLeftRight,
    color: '#7c3aed',
    bg: 'rgba(124, 58, 237, 0.08)',
  },
  schema: {
    label: 'Schema',
    plural: 'Schemas',
    icon: LayoutList,
    color: '#b8654a',
    bg: 'rgba(184, 101, 74, 0.08)',
  },
  file: {
    label: 'File',
    plural: 'Files',
    icon: File,
    color: '#6b5f50',
    bg: 'rgba(107, 95, 80, 0.08)',
  },

  // --- People & groups ---
  person: {
    label: 'Person',
    plural: 'People',
    icon: User,
    color: '#2d6a8a',
    bg: 'rgba(45, 106, 138, 0.10)',
  },
  friend: {
    label: 'Friend',
    plural: 'Friends',
    icon: UserCheck,
    color: '#2d6a8a',
    bg: 'rgba(45, 106, 138, 0.10)',
  },
  group: {
    label: 'Group',
    plural: 'Groups',
    icon: Users,
    color: '#b7770d',
    bg: 'rgba(183, 119, 13, 0.08)',
  },
  group_member: {
    label: 'Group',
    plural: 'Groups',
    icon: Users,
    color: '#b7770d',
    bg: 'rgba(183, 119, 13, 0.08)',
  },
  group_connection: {
    label: 'Group',
    plural: 'Groups',
    icon: Users,
    color: '#b7770d',
    bg: 'rgba(183, 119, 13, 0.08)',
  },

  // --- Apps & integrations ---
  app: {
    label: 'App',
    plural: 'Apps',
    icon: AppWindow,
    color: '#5a7a5a',
    bg: 'rgba(90, 122, 90, 0.08)',
  },
  // A named delegate an app declares under its own EP (sub_entity:declare).
  // It holds no authority of its own — its ceiling is the parent app's,
  // intersected with the user's consent — so anywhere a sub-entity is shown,
  // the parent app must be shown with it.
  sub_entity: {
    label: 'Sub-entity',
    plural: 'Sub-entities',
    icon: Bot,
    color: '#7c3aed',
    bg: 'rgba(124, 58, 237, 0.08)',
  },
  plugin: {
    label: 'Plugin',
    plural: 'Plugins',
    icon: Puzzle,
    color: '#d97706',
    bg: 'rgba(217, 119, 6, 0.08)',
  },
  automation: {
    label: 'Automation',
    plural: 'Automations',
    icon: Zap,
    color: '#2d6a8a',
    bg: 'rgba(45, 106, 138, 0.10)',
  },

  // --- Permissions & privacy ---
  authorization: {
    label: 'Exposure Profile',
    plural: 'Exposure Profiles',
    icon: KeyRound,
    color: '#5a7a5a',
    bg: 'rgba(90, 122, 90, 0.08)',
  },
  privacy_realm: {
    label: 'Realm',
    plural: 'Realms',
    icon: ShieldCheck,
    color: '#5a7a5a',
    bg: 'rgba(90, 122, 90, 0.08)',
  },

  // --- Capabilities (used in FormA / ProfileBadges) ---
  discovery: {
    label: 'Discovery',
    plural: 'Discovery',
    icon: Search,
    color: '#db2777',
    bg: 'rgba(219, 39, 119, 0.08)',
  },
};

// ---------------------------------------------------------------------------
// Action Icons  (CRUD + common operations)
// Each value is a React element — render inline: {ACTION_ICONS.edit}
// ---------------------------------------------------------------------------

const ACTION_SIZE = 14;

export const ACTION_ICONS = {
  create:  <Plus size={ACTION_SIZE} />,
  edit:    <Pencil size={ACTION_SIZE} />,
  delete:  <Trash2 size={ACTION_SIZE} />,
  view:    <Eye size={ACTION_SIZE} />,
  share:   <Share2 size={ACTION_SIZE} />,
  link:    <Link size={ACTION_SIZE} />,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const FALLBACK = {
  label: 'Unknown',
  plural: 'Unknown',
  icon: HelpCircle,
  color: '#a09585',
  bg: 'rgba(160, 149, 133, 0.08)',
};

export function getEntity(type) {
  return ENTITY_TYPES[type] || FALLBACK;
}

/**
 * Returns a rendered Lucide icon element for the given entity type.
 * @param {string} type - Entity type key
 * @param {number} size - Icon size in px (default 14)
 */
export function getEntityIcon(type, size = 14) {
  const IconComponent = (ENTITY_TYPES[type] || FALLBACK).icon;
  return <IconComponent size={size} />;
}

export function getEntityLabel(type, plural = false) {
  const e = ENTITY_TYPES[type] || FALLBACK;
  return plural ? e.plural : e.label;
}

export function getEntityColor(type) {
  return (ENTITY_TYPES[type] || FALLBACK).color;
}

export function getEntityBg(type) {
  return (ENTITY_TYPES[type] || FALLBACK).bg;
}

/**
 * Returns the bg tint for a given hex color (10% opacity).
 */
export function colorToBg(hex) {
  if (!hex) return null;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, 0.10)`;
}

export { ENTITY_TYPES };
export default ENTITY_TYPES;
